



































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import { ErrorsMixin } from "@/employer/components/shared/ErrorsMixin";
import { mixins } from "vue-class-component";
import { formatCurrency } from "@/shared/currency";
import {
  FlatManagedPortfolioResponse,
  DrivewealthSweep,
  DrivewealthSweepStatus, DrivewealthWizardStep,
  DrivewealthAccountManagementType
} from "@/shared/types";
import { Validator } from "vee-validate";
import InstrumentName from "../InstrumentName.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import WizardNavigation from "@/employee/components/investments/drivewealth/wizard/WizardNavigation.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";

@Component({
  components: {
    InstrumentName,
    GenericLoadingSpinner,
    WizardNavigation
  }
})
export default class SweepManaged extends mixins(ErrorsMixin) {

  notifierStore = useNotifierStore();
  @Prop({ required: true })
  isPreenrollment: boolean;

  formatCurrency = formatCurrency;
  $validator: Validator;

  isActive = false;
  threshold = 0;
  isDirty = false;
  disableRestrictedMode = false;

  drivewealthStore = useDrivewealthStore();
  drivewealthSweepStore = useDrivewealthSweepStore();

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Watch("$props", { deep: true })
  loadTable () {
    this.drivewealthStore.loadManagedAccount({ username: this.username, partnerId: this.partnerId });
    this.drivewealthSweepStore.getSweep({ username: this.username, partnerId: this.partnerId });
    this.drivewealthStore.loadHsaBalanceAndThreshold({ username: this.username, partnerId: this.partnerId });
  }

  mounted (): void {
    this.loadTable();
    this.initializeSweep();
  }

  get wizardStep () {
    return DrivewealthWizardStep.AutomatedFunding;
  }

  get restrictedMode () {
    return this.isPreenrollment && !this.disableRestrictedMode;
  }
  get saveThresholdVisible () {
    return !this.isPreenrollment;
  }

  get isLoaded (): boolean {
    return this.drivewealthStore.managedAccountLoaded &&
      this.drivewealthSweepStore.sweepLoaded &&
      this.drivewealthStore.isHsaBalanceAndThresholdLoaded(null);
  }

  @Watch("isDirty")
  onDirtyChanged () {
    this.updateNavigationGuard(this.isDirty);
  }

  updateNavigationGuard (confirmNavigation: boolean) {
    const eventData = {
      confirmNavigation,
      message: "Are you sure you want to leave?  Your unsaved changes will be lost if you continue.  Click Cancel to return to the page to save your changes."
    };
    this.$emit("navigationGuardChange", eventData);
  }

  @Watch("drivewealthSweepStore.sweepLoaded")
  initializeSweep () {
    if (!this.drivewealthSweepStore.sweepLoaded) {
      return;
    }
    this.threshold = this.drivewealthSweepStore.sweepThreshold;
    this.isActive = this.drivewealthSweepStore.sweepIsActive;

    if (!this.isActive && this.threshold < 0.01) {
      this.threshold = this.drivewealthStore.hsaInvestmentThreshold;
    }

    if (this.drivewealthSweepStore.sweepIsActive) {
      this.disableRestrictedMode = true;
    }
  }

  holdingFields = [
    {
      key: "instrument",
      label: "Investment",
      thClass: "text-left pl-2",
      tdClass: "pl-2"
    },
    {
      key: "fund_type",
      label: "Fund Type",
      thClass: "text-center",
      tdClass: "text-center"
    },
    {
      key: "allocation",
      label: "Target Allocation",
      thClass: "text-right",
      tdClass: "text-right"
    }
  ];
  get holdingItems () {
    if (!this.drivewealthStore.managedAccount) {
      return [];
    }
    return this.drivewealthStore.managedAccount.instruments.map(h => ({
      instrument: { symbol: h.stockSymbol, companyName: h.fundName, website: h.webSite, imageUrl: h.imageUrl },
      fund_type: h.fundType,
      percentage: (h.target * 100).toFixed(2)
    }));
  }

  get thresholdValidator () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: this.drivewealthStore.hsaInvestmentThreshold
    };
  }

  onDirtied () {
    this.isDirty = true;
  }

  validateAll () {
    return this.$validator.validateAll()
      .then(result => {
        if (!result) return Promise.resolve(false);
        return this.$validator.validate("threshold"); // sometimes this is disabled, and we still want to validate it
      });
  }

  validationErrorOrNull () {
    if (this.threshold < this.drivewealthStore.hsaInvestmentThreshold) {
      return `The threshold field must be ${formatCurrency(this.drivewealthStore.hsaInvestmentThreshold)} or more.`;
    }
    return null;
  }

  get hasValidationError () {
    return this.isDirty && !!this.validationErrorOrNull();
  }

  onActiveChanged () {
    const validationErrorOrNull = this.validationErrorOrNull();

    if (validationErrorOrNull) {
      this.notifierStore.setDanger(validationErrorOrNull);
      this.isActive = !this.isActive;
      return;
    }

    this.drivewealthSweepStore.postSweepStatus({
      isActive: this.isActive
    })
      .then(_ => {
        this.notifierStore.setSuccess(`Automatic funding is now ${this.isActive ? "" : "in"}active.`);
        return Promise.resolve();
      })
      .catch(err => {
        this.notifierStore.setDanger(`Failed to ${this.isActive ? "" : "in"}activate automated funding`);
        this.isActive = !this.isActive;
        return Promise.resolve();
      });
  }

  onCancel () {
    this.updateNavigationGuard(false);
    this.$emit("complete");
  }

  onSubmit () {
    this.$validator.validateAll()
      .then(result => {
        if (!result) return;

        this.submit();
      });
  }

  submit () {
    if (this.isSubmitting) return;

    this.isSubmitting = true;

    const submitWithIsActive = this.restrictedMode || this.isActive;
    const sweep: DrivewealthSweep = {
      isActive: submitWithIsActive,
      threshold: this.threshold,
      isPreenrollment: this.isPreenrollment,
      funds: []
    };

    this.drivewealthSweepStore.postSweep(sweep)
      .then(_ => {
        this.isSubmitting = false;
        this.updateNavigationGuard(false);
        this.notifierStore.setSuccess("Automatic investment updated.");
        this.$emit("complete");
      })
      .catch(this.handleError);
  }
}
